import React, { useState } from "react";
import { Card, Badge, Button, Dropdown, Modal, Alert } from "react-bootstrap";
import {
  formatPaymentMethodDisplay,
  getPaymentMethodIcon,
  isPaymentMethodExpired,
  isPaymentMethodExpiringSoon,
  getPaymentMethodStatusColor,
  formatPaymentMethodExpiry,
} from "../api";

/**
 * PaymentMethodCard Component
 * Issue #11: Payment Method Management
 *
 * Displays individual payment method with actions
 */
const PaymentMethodCard = ({
  paymentMethod,
  onSetDefault,
  onDelete,
  onUpdate,
  isDefault = false,
  showActions = true,
  className = "",
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actionLoading, setActionLoading] = useState("");

  if (!paymentMethod) {
    return null;
  }

  const isExpired = isPaymentMethodExpired(paymentMethod);
  const isExpiringSoon = isPaymentMethodExpiringSoon(paymentMethod);
  const statusColor = getPaymentMethodStatusColor(paymentMethod);

  const handleSetDefault = async () => {
    if (isDefault || !onSetDefault) return;

    setActionLoading("setting-default");
    try {
      await onSetDefault(paymentMethod.id);
    } catch (error) {
      console.error("Failed to set default payment method:", error);
    } finally {
      setActionLoading("");
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    setActionLoading("deleting");
    try {
      await onDelete(paymentMethod.id);
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Failed to delete payment method:", error);
    } finally {
      setActionLoading("");
    }
  };

  const handleUpdate = () => {
    if (onUpdate) {
      onUpdate(paymentMethod.id);
    }
  };

  return (
    <>
      <Card className={`payment-method-card ${className}`}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start">
            <div className="d-flex align-items-center">
              <div className="me-3">
                <i
                  className={`${getPaymentMethodIcon(paymentMethod)} text-${
                    paymentMethod.type === "card" ? "primary" : "secondary"
                  }`}
                  style={{ fontSize: "2rem" }}
                ></i>
              </div>

              <div>
                <h6 className="mb-1">
                  {formatPaymentMethodDisplay(paymentMethod)}
                  {isDefault && (
                    <Badge bg="success" className="ms-2">
                      Default
                    </Badge>
                  )}
                </h6>

                {paymentMethod.type === "card" && paymentMethod.card && (
                  <div>
                    <small className="text-muted">
                      Expires {formatPaymentMethodExpiry(paymentMethod)}
                    </small>
                    {(isExpired || isExpiringSoon) && (
                      <div className="mt-1">
                        <Badge bg={statusColor}>
                          {isExpired ? "Expired" : "Expiring Soon"}
                        </Badge>
                      </div>
                    )}
                  </div>
                )}

                {paymentMethod.usage_stats && (
                  <div className="mt-2">
                    <small className="text-muted">
                      Used {paymentMethod.usage_stats.count || 0} times
                      {paymentMethod.usage_stats.last_used && (
                        <span>
                          {" "}
                          • Last used{" "}
                          {new Date(
                            paymentMethod.usage_stats.last_used * 1000
                          ).toLocaleDateString()}
                        </span>
                      )}
                    </small>
                  </div>
                )}
              </div>
            </div>

            {showActions && (
              <Dropdown align="end">
                <Dropdown.Toggle
                  variant="outline-secondary"
                  size="sm"
                  id={`payment-method-actions-${paymentMethod.id}`}
                >
                  <i className="fas fa-ellipsis-v"></i>
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  {!isDefault && (
                    <Dropdown.Item
                      onClick={handleSetDefault}
                      disabled={actionLoading === "setting-default"}
                    >
                      <i className="fas fa-star me-2"></i>
                      {actionLoading === "setting-default"
                        ? "Setting as Default..."
                        : "Set as Default"}
                    </Dropdown.Item>
                  )}

                  <Dropdown.Item onClick={handleUpdate}>
                    <i className="fas fa-edit me-2"></i>
                    View Details
                  </Dropdown.Item>

                  <Dropdown.Divider />

                  <Dropdown.Item
                    onClick={() => setShowDeleteModal(true)}
                    className="text-danger"
                    disabled={actionLoading === "deleting"}
                  >
                    <i className="fas fa-trash me-2"></i>
                    {actionLoading === "deleting" ? "Deleting..." : "Delete"}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>

          {/* Warning for expired/expiring cards */}
          {(isExpired || isExpiringSoon) && (
            <Alert variant={statusColor} className="mt-3 mb-0 py-2">
              <small>
                <i className="fas fa-exclamation-triangle me-1"></i>
                {isExpired
                  ? "This card has expired. Please update your payment method."
                  : "This card expires soon. Consider updating your payment method."}
              </small>
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Payment Method</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete{" "}
            <strong>{formatPaymentMethodDisplay(paymentMethod)}</strong>?
          </p>
          {isDefault && (
            <Alert variant="warning">
              <small>
                <i className="fas fa-exclamation-triangle me-1"></i>
                This is your default payment method. If you have other payment
                methods, one will be automatically set as the new default.
              </small>
            </Alert>
          )}
          <p className="text-muted">
            <small>This action cannot be undone.</small>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={actionLoading === "deleting"}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={actionLoading === "deleting"}
          >
            {actionLoading === "deleting" ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                ></span>
                Deleting...
              </>
            ) : (
              "Delete Payment Method"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PaymentMethodCard;
